import { PushPin } from '@mui/icons-material';
import { Box, Tooltip, Typography, Avatar } from '@mui/material';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { useFetchUserpreferenceQuery } from '../../redux/apis/listUserPreference.api';
import { createUserPreference } from '../../graphql';
import { API, graphqlOperation } from 'aws-amplify';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import FullscreenButton from '../Buttons/FullSceenBtn';
import useControlledNavigation from '../../hooks/useControlledNavigation';

const BottomNavBar = () => {
  const [pin, setPin] = useState(true);
  const [showNavBar, setShowNavBar] = useState(false);
  const hideTimeoutRef = useRef(null);
  const hidePinTimout = useRef(null);
  const clickCountTimeoutRef = useRef(null);
  const [activeNav, setActiveNav] = useState('');
  const [activeSubNav, setActiveSubNav] = useState('');
  const [inputType, setInputType] = useState('mouse');
  const [clickCount, setClickCount] = useState(0);
  const navBarRef = useRef(null);
  const sidePinRef = useRef(null);

  const { data: userPreferenceData, error, isLoading, refetch } = useFetchUserpreferenceQuery();
  const location = useLocation();
  const navigate = useControlledNavigation();

  const lastX = useRef(null);
  const lastDirection = useRef(null);
  const swipeCount = useRef(0);
  const swipeTimeoutRef = useRef(null);
  const initialDirectionRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (lastX.current === null) {
        lastX.current = e.clientX;
        return;
      }

      let direction = null;

      if (e.clientX > lastX.current) direction = "right";
      else if (e.clientX < lastX.current) direction = "left";

      if (direction && direction !== lastDirection.current) {
        if (swipeTimeoutRef.current) {
          clearTimeout(swipeTimeoutRef.current);
          swipeTimeoutRef.current = null;
        }

        if (initialDirectionRef.current === null) {
          initialDirectionRef.current = direction;
          lastDirection.current = direction;
        } else {
          const isCompleteSwipe = 
            (initialDirectionRef.current === "right" && direction === "left") ||
            (initialDirectionRef.current === "left" && direction === "right");

          if (isCompleteSwipe) {
            swipeCount.current++;
          
            initialDirectionRef.current = direction;
            lastDirection.current = direction;

            if (swipeCount.current === 3) {
              setShowNavBar(true);
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
              }
              hideTimeoutRef.current = setTimeout(() => {
                if (inputType === 'mouse' && !pin) {
                  setShowNavBar(false);
                }
                hideTimeoutRef.current = null;
              }, 3000);
              
              swipeCount.current = 0;
              initialDirectionRef.current = null;
              lastDirection.current = null;
              lastX.current = null;
              
              if (swipeTimeoutRef.current) {
                clearTimeout(swipeTimeoutRef.current);
                swipeTimeoutRef.current = null;
              }
              return;
            }
          } else {
            swipeCount.current = 0;
            initialDirectionRef.current = direction;
            lastDirection.current = direction;
          }
        }
        swipeTimeoutRef.current = setTimeout(() => {
          swipeCount.current = 0;
          initialDirectionRef.current = null;
          lastDirection.current = null;
          lastX.current = null;
          swipeTimeoutRef.current = null;
        }, 200);
      }

      lastX.current = e.clientX;
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current);
      }
    };
  }, [pin]);

  const pinFn = (newPinState) => {
    API.graphql(
      graphqlOperation(createUserPreference, {
        createUserPreferenceInput: {
          attributetypecd: 'SideBarPin',
          attributevalue: newPinState ? 0 : 1,
          additionalvalue: '',
        },
      })
    )
      .then((r) => {})
      .catch((e) => {
        console.log(e, 'error');
      });
  };

  useEffect(() => {
    if (userPreferenceData) {
      let value = userPreferenceData.find((e) => e.attributetypecd === 'SideBarPin')?.attributevalue;
      setPin(value === undefined ? true : value == 0 ? true : false);
    }
  }, [userPreferenceData]);

  const handleMouseEnter = () => {
    setInputType('mouse');
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (hidePinTimout.current) {
      clearTimeout(hidePinTimout.current);
      hidePinTimout.current = null;
    }
    setShowNavBar(true);
  };

  const handleMouseLeave = () => {
    if (inputType === 'mouse') {
      hideTimeoutRef.current = setTimeout(() => {
        setShowNavBar(false);
      }, 1000);
    }
  };

  const handlePinMouseLeave = () => {
    if (inputType === 'mouse') {
      hidePinTimout.current = setTimeout(() => {
        setShowNavBar(false);
      }, 500);
    }
  };

  const handleTouchStart = () => {
    setInputType('touch');
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (hidePinTimout.current) {
      clearTimeout(hidePinTimout.current);
      hidePinTimout.current = null;
    }
    setShowNavBar(true);
  };

  const handlePinClick = () => {
    const newPinState = !pin;
    setPin(newPinState);
    pinFn(newPinState);

    // When unpinning, explicitly hide the nav bar
    if (!newPinState) {
      setShowNavBar(false);
      // Clear any pending timeouts
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      if (hidePinTimout.current) {
        clearTimeout(hidePinTimout.current);
        hidePinTimout.current = null;
      }
    }
  };

  const handleSidePinClick = () => {
    if (inputType === 'touch') setShowNavBar(!showNavBar);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (hidePinTimout.current) {
      clearTimeout(hidePinTimout.current);
      hidePinTimout.current = null;
    }
  };

  const handlePinClickStay = (e) => {
    if (e.pointerType === 'mouse') {
      if (clickCountTimeoutRef.current) {
        clearTimeout(clickCountTimeoutRef.current);
        clickCountTimeoutRef.current = null;
      }
      handlePinClick();
      return;
    }

    if (clickCount === 1) {
      if (clickCountTimeoutRef.current) {
        clearTimeout(clickCountTimeoutRef.current);
        clickCountTimeoutRef.current = null;
      }
      handlePinClick();
      setClickCount(0);
      return;
    }

    if (clickCountTimeoutRef.current) {
      clearTimeout(clickCountTimeoutRef.current);
      clickCountTimeoutRef.current = null;
    }

    setClickCount(1);

    clickCountTimeoutRef.current = setTimeout(() => {
      setClickCount(0);
      clickCountTimeoutRef.current = null;
    }, 500);
  };

  const iconStyle = useMemo(
    () => ({
      color: '#ffffff',
      fontSize: '10px',
      fontWeight: 'bold',
      borderRadius: '3px',
      height: '15px',
      width: '30px',
      backgroundColor: '#911B7C',
    }),
    []
  );

  const menus = useMemo(
    () => [
      {
        menu: 'dashboard',
        title: 'Dashboard',
        mainNav: '/?active=Business_Overview',
        tooltip: '',
        toolTipMain: '',
        // icon: <Avatar variant="rounded" sx={{ color: state.openMenu.dashboard && (state.selectMenu == 'dashboard') ? '#ffffff' : '#ffffff', fontSize: '12px', fontWeight: 'bold', backgroundColor: 'rgb(255 255 255 / 20%)', borderRadius: '3px', height: '30px', width: '30px' }}>DaB</Avatar>,
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            DaB
          </Avatar>
        ),
        //   submenuColor:theme.palette.secondary.main,
        submenus: [],
        nav: '/?active=Business_Overview',
      },
      {
        menu: 'inventory',
        title: 'Items',
        mainNav: '/newItemList',
        subNav: '/additem',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Item',
        toolTipMain: 'View Item List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Itm
          </Avatar>
        ),
        //   submenuColor:theme.palette.secondary.main,
        submenus: [],
      },
      {
        menu: 'customer',
        title: 'Customer',
        mainNav: '/customerList',
        subNav: '/addcustomer',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Customer',
        toolTipMain: 'View Customer List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Cus
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:cousColor,
      },
      {
        menu: 'Sales',
        title: 'Sales',
        mainNav: '/salesInvoiceReport',
        subNav:
          // gstStatus == 1 ? "/addinvoiceNonTaxable" : "/addinvoiceTaxable",
          '/addnewinvoicetax',
        //   subNavDisabled: Freeze,
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        toolTipMain: 'View Sales Invoice List',
        tooltip: 'Add Invoice',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Sinv
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:cousColor,
      },
      {
        menu: 'POS',
        title: 'POS',
        mainNav: '/allpos',
        subNav: '/newpos',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        toolTipMain: 'View POS Invoice List',
        tooltip: 'Add Pos',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Pos
          </Avatar>
        ),
        submenus: [],
        //    submenuColor:cousColor,
      },
      {
        menu: 'receipts',
        title: 'Receipts',
        mainNav: '/receipts',
        subNav: '/addreceipts',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Receipts',
        toolTipMain: 'View Receipt List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Rec
          </Avatar>
        ),
        submenus: [
          // {
          //   name: 'Receipts',
          //   nav: '/receipts'
          // },
          // {
          //   name: 'Add Receipts',
          //   nav: '/addreceipts'
          // },
        ],
        //    submenuColor:cousColor,
      },
      {
        menu: 'supplier',
        title: 'Supplier',
        mainNav: '/supplierList',
        subNav: '/addsupplier',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Supplier',
        toolTipMain: 'View Supplier List',
        icon: (
          <Avatar
            variant="rounded"
            sx={iconStyle}
            // sx={menuStyle}
          >
            Sup
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:supColor,
      },
      {
        menu: 'Purchase Invoices',
        title: 'Purchase',
        mainNav: '/allpurchaseandexpense',
        subNav:
          // gstStatus == 1
          //   ? "/addpurchaseandexpensenontaxable"
          //   : "/addpurchaseandexpensetaxable",
          '/addnewpurchasetax',
        //   subNavDisabled: Freeze,
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        toolTipMain: 'View Purchase Invoice List',
        tooltip: 'Add Purchase Invoice',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Pinv
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:supColor,
      },
      {
        menu: 'payments',
        title: 'Payments',
        mainNav: '/paymententries',
        subNav: '/addpaymententries',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Payment',
        toolTipMain: 'View Payment List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Pay
          </Avatar>
        ),
        submenus: [
          // {
          //   name: 'Payment Entries',
          //   nav: '/paymententries'
          // },
          // {
          //   name: 'Add Payment Entries',
          //   nav: '/addpaymententries'
          // },
        ],
        //   submenuColor:supColor,
      },
      {
        menu: 'Debit Note /Purchase Return',
        title: 'Debit Note',
        mainNav: '/alldebitnote',
        subNav: '/adddebitnote',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Debit Note',
        toolTipMain: 'View Debit Note List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Drn
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:dbnColor,
      },
      {
        menu: 'Credit Note / Sales Return',
        title: 'Credit Note',
        mainNav: '/allcreditnote',
        subNav: '/addcreditnote',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Credit Note',
        toolTipMain: 'View Credit Note List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Crn
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:dbnColor,
      },
      {
        menu: 'Journal Entries',
        title: 'Journal',
        mainNav: '/journalentries',
        subNav: '/addjournalentry',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Journal Entries',
        toolTipMain: 'View Journal Entries List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            JV
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:jvColor,
      },
      {
        menu: 'Opening Balance',
        title: 'Op. Bal.',
        mainNav: '/openingbalance',
        subNav: '/openingbalance',
        subNAvIcon: (
          <AddIcon
            sx={{
              background: 'transparent',
              width: '12px',
              height: '12px',
              color: '#fb3b6fff',
              borderRadius: '2px',
            }}
          />
        ),
        //   subNavDisabled: Freeze,
        tooltip: 'Add Opening Balance',
        toolTipMain: 'View Opening Balance List',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            OpB
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:jvColor,
      },
      {
        menu: 'accounts',
        title: 'Accounts',
        mainNav: '/accounts',
        toolTipMain: 'View Accounts ',
        // subNav: '',
        tooltip: '',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Acc
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:jvColor,
      },
      {
        menu: 'reports',
        title: 'Reports',
        mainNav: '/reports',
        // subNav: '',
        tooltip: '',
        toolTipMain: 'View Reports',
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            Rpt
          </Avatar>
        ),
        submenus: [],
        //   submenuColor:jvColor,
      },
      {
        menu: 'profitAndLose',
        title: 'P&L',
        mainNav: '/?active=ProfitAndLoss',
        tooltip: '',
        toolTipMain: '',
        // icon: <Avatar variant="rounded" sx={{ color: state.openMenu.dashboard && (state.selectMenu == 'dashboard') ? '#ffffff' : '#ffffff', fontSize: '12px', fontWeight: 'bold', backgroundColor: 'rgb(255 255 255 / 20%)', borderRadius: '3px', height: '30px', width: '30px' }}>DaB</Avatar>,
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            P&L
          </Avatar>
        ),
        //   submenuColor:theme.palette.secondary.main,
        submenus: [],
        // nav: '/?active=Business_Overview',
      },
      {
        menu: 'balanceSheet',
        title: 'BS',
        mainNav: '/?active=BalanceSheet',
        tooltip: '',
        toolTipMain: '',
        // icon: <Avatar variant="rounded" sx={{ color: state.openMenu.dashboard && (state.selectMenu == 'dashboard') ? '#ffffff' : '#ffffff', fontSize: '12px', fontWeight: 'bold', backgroundColor: 'rgb(255 255 255 / 20%)', borderRadius: '3px', height: '30px', width: '30px' }}>DaB</Avatar>,
        icon: (
          <Avatar variant="rounded" sx={iconStyle}>
            BS
          </Avatar>
        ),
        //   submenuColor:theme.palette.secondary.main,
        submenus: [],
        // nav: '/?active=BalanceSheet',
      },
    ],
    []
  );

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const matchedMenu = menus.find(
      (item) => (item.subNav && currentPath === item.subNav) || (item.mainNav && currentPath === item.mainNav)
    );

    if (matchedMenu) {
      if (currentPath === matchedMenu.subNav) {
        setActiveSubNav(matchedMenu.subNav);
        setActiveNav('');
      } else {
        setActiveSubNav('');
        setActiveNav(matchedMenu.mainNav);
      }
    }
    if (currentPath == '/') {
      setActiveNav('/?active=Business_Overview');
      setActiveSubNav('');
    }
  }, [location]);

  useEffect(() => {
    const handleTouchStart = () => setInputType('touch');
    const handleMouseMove = () => setInputType('mouse');

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pin === false &&
        navBarRef.current &&
        sidePinRef.current &&
        !navBarRef.current.contains(event.target) &&
        !sidePinRef.current.contains(event.target)
      ) {
        setShowNavBar(false);
        setClickCount(0); // Reset click count when clicking outside
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        if (hidePinTimout.current) {
          clearTimeout(hidePinTimout.current);
          hidePinTimout.current = null;
        }
        if (clickCountTimeoutRef.current) {
          clearTimeout(clickCountTimeoutRef.current);
          clickCountTimeoutRef.current = null;
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [pin]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      if (hidePinTimout.current) {
        clearTimeout(hidePinTimout.current);
        hidePinTimout.current = null;
      }
      if (clickCountTimeoutRef.current) {
        clearTimeout(clickCountTimeoutRef.current);
        clickCountTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <Box>
      <Box
        ref={navBarRef}
        sx={{
          position: 'fixed',
          // bottom: pin || showNavBar ? '10px' : '-100px',
          bottom: '10px',
          left: '5%',
          right: '5%',
          width: '90%',
          zIndex: '10000',
          height: '7vh',
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          borderRadius: '20px',
          backgroundColor: 'rgba(239, 239, 239, 0.74)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.53)',
          boxShadow: '0 0px 5px rgba(0, 0, 0, 0.63)',
          pointerEvents: 'auto',
          transform: pin || showNavBar ? 'translateY(0)' : 'translateY(130%)',
          transition: 'transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
      >
        {menus.map((item, index) => (
          <Box key={index} sx={{ pointerEvents: 'auto' }}>
            <Box
              className="icon_box"
              sx={{
                // width: '70px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '2px',
                height: '100%',
              }}
            >
              <Box sx={{ height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '5px' }}>
                {item?.subNav?.length > 0 ? (
                  <Tooltip
                    title={item.tooltip}
                    placement='top'
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'rgba(239, 239, 239, 0.74)',
                          backdropFilter: 'blur(3px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          borderTop: '1px solid rgba(255, 255, 255, 0.53)',
                          boxShadow: '0 0px 5px rgba(0, 0, 0, 0.63)',
                          color: '#911B7C',
                          fontWeight: 'bold',
                        },
                      },
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.subNav);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        border: activeSubNav === item.subNav ? '2px solid #fb3b6fff' : '2px solid transparent',
                        padding: '2px',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          // animation: 'hoverBounce 0.3s ease',
                          transform: activeSubNav === item.subNav ? '' : 'scale(1.5)',
                        },
                      }}
                    >
                      {item.subNAvIcon}
                    </Box>
                  </Tooltip>
                ) : null}
              </Box>
              <Box sx={{ height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.mainNav) {
                      navigate(item.mainNav);
                    }
                  }}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: activeNav === item.mainNav ? 'red' : 'black',
                    lineHeight: 1,
                    transition: 'transform 0.3s ease',
                    cursor: item.mainNav ? 'pointer' : 'default',
                    //   '@keyframes hoverBounce': {
                    //     '0%': { transform: 'scale(1)' },
                    //     '50%': { transform: 'scale(1.3)' },
                    //     '100%': { transform: 'scale(1)' },
                    //   },
                    '&:hover': {
                      // animation: 'hoverBounce 0.3s ease',
                      transform: item.mainNav ? 'scale(1.3)' : 'scale(1)',
                    },
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}

        {/* <Box
          sx={{
            // position: 'fixed',
            // bottom: '17px',
            // left: '15px',
            height: '23px',
            width: '24px',
            //   backgroundColor: '#911B7C',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            backgroundColor: 'rgba(239, 239, 239, 0.74)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.53)',
            boxShadow: '0 0px 5px rgba(0, 0, 0, 0.63)',
            pointerEvents: 'auto',
            cursor: 'pointer',
            // zIndex: '10001',
          }}
          onClick={handlePinClick}
          onTouchStart={(e) => {
            e.stopPropagation();
            handleTouchStart();
          }}
          className="navPin"
        >
          <PushPin
            sx={{
              transition: 'transform 0.5s ease',
              transform: pin ? 'rotate(0deg)' : 'rotate(45deg)',
              fontSize: '16px',
              color: pin ? 'red' : 'gray',
            }}
          />
        </Box> */}
        <FullscreenButton matches={true} />
      </Box>
      <Box
        ref={sidePinRef}
        className="navPin"
        sx={{
          position: 'fixed',
          right: '5px',
          bottom: '20px',
          height: '5%',
          width: '2%',
          backgroundColor: 'rgba(239, 239, 239, 0.74)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: '10000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // transform: !pin ? 'translateX(0)' : 'translateX(120%)',
          transition: 'transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '50%',
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.53)',
          cursor: 'pointer',
        }}
        onMouseEnter={() => {
          setInputType('mouse');
          setShowNavBar(true);
        }}
        onMouseLeave={handlePinMouseLeave}
        // onPointerDown={handlePinClickStay}
        onClick={handlePinClick}
        // onTouchStart={(e) => {
        //   e.stopPropagation();
        //   handleSidePinClick();
        // }}
      >
        <PushPin
          sx={{
            transform: pin ? 'rotate(0deg)' : 'rotate(45deg)',
            fontSize: '18px',
            color: pin ? 'red' : 'gray',
            transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      </Box>
    </Box>
  );
};

export default BottomNavBar;
